class Tabs extends HTMLElement {
	#elements;

	/**
	 * Create tabs custom element
	 */
	constructor() {
		super();

		this.#elements = {
			tabs: /** @type {NodeListOf<HTMLButtonElement>} */ (
				this.querySelectorAll('[role="tab"]')
			),
			tabpanels: /** @type {NodeListOf<HTMLDivElement>} */ (
				this.querySelectorAll('[role="tabpanel"]')
			),
		};

		this.#addEventListeners();
	}

	/**
	 * Add event listeners
	 */
	#addEventListeners() {
		this.#elements.tabs.forEach((tab) => {
			tab.addEventListener("click", this.#onTabClick.bind(this));
			tab.addEventListener("keydown", this.#onTabKeydown.bind(this));
		});
	}

	/**
	 * Handle tab click event
	 * @param {PointerEvent} event
	 */
	#onTabClick(event) {
		const tab = /** @type {HTMLButtonElement} */ (event.currentTarget);
		const newTabIndex = [...this.#elements.tabs].indexOf(tab);

		this.#setActiveTab(newTabIndex);
	}

	/**
	 * Handle tab keydown event
	 * @param {KeyboardEvent} event
	 */
	#onTabKeydown(event) {
		const tab = /** @type {HTMLButtonElement} */ (event.currentTarget);
		const isRTL = tab.matches(":dir(rtl)");

		/** @type {number|undefined} */
		let newTabIndex;

		switch (event.key) {
			case "ArrowLeft": {
				if (isRTL) {
					newTabIndex = this.#nextTabIndex;
				} else {
					newTabIndex = this.#previousTabIndex;
				}
				break;
			}

			case "ArrowRight": {
				if (isRTL) {
					newTabIndex = this.#previousTabIndex;
				} else {
					newTabIndex = this.#nextTabIndex;
				}
				break;
			}

			case "Home": {
				newTabIndex = 0;
				break;
			}

			case "End": {
				newTabIndex = this.#elements.tabs.length - 1;
				break;
			}
		}

		if (newTabIndex !== undefined) {
			event.stopPropagation();
			event.preventDefault();

			this.#setActiveTab(newTabIndex);
		}
	}

	/**
	 * Get active tab index
	 * @returns {number}
	 */
	get #activeTabIndex() {
		return [...this.#elements.tabs].findIndex(
			(tab) => tab.getAttribute("aria-selected") === "true",
		);
	}

	/**
	 * Get previous tab index
	 * @returns {number}
	 */
	get #previousTabIndex() {
		if (this.#activeTabIndex === 0) {
			return this.#elements.tabs.length - 1;
		}

		return this.#activeTabIndex - 1;
	}

	/**
	 * Get next tab index
	 * @returns {number}
	 */
	get #nextTabIndex() {
		if (this.#activeTabIndex === this.#elements.tabs.length - 1) {
			return 0;
		}

		return this.#activeTabIndex + 1;
	}

	/**
	 * Set active tab by index
	 * @param {number} newTabIndex
	 */
	#setActiveTab(newTabIndex) {
		this.#elements.tabs.forEach((tab, index) => {
			if (index === newTabIndex) {
				tab.setAttribute("aria-selected", "true");
				tab.removeAttribute("tabindex");
				tab.focus();
			} else {
				tab.setAttribute("aria-selected", "false");
				tab.setAttribute("tabindex", "-1");
			}
		});

		this.#elements.tabpanels.forEach((tabpanel, index) => {
			tabpanel.toggleAttribute("hidden", index !== newTabIndex);
		});
	}
}

customElements.define("cd-tabs", Tabs);
